import React from 'react'
import { IconEyeExclamation, IconReplace } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { cloudAccessSchema } from '../organization.schema'

const ACCESS_SHORT_LABELS = {
  'Balto Cloud Access': 'Cloud Access',
  'Playbook Management': 'Playbook Mgmt',
  'User Management': 'User Mgmt',
  'Leaderboard Management': 'Leaderboard Mgmt',
  'QA Access': 'QA',
  'Quality Access': 'Quality',
  'Real-Time Coaching Access': 'RT Coaching',
  'Hierarchy Manager': 'Hierarchy Mgr',
}

const MAX_VISIBLE_PERMISSIONS = 3

const COLUMN_FILTER_PROPS = {
  popupPosition: 'bottom left',
  autoFocus: false,
  applyOnChange: true,
  isSelectAll: true,
}

const findTagCategoryForTag = (tag, tagCategories) => {
  return tagCategories.some((category) => category.id === tag.tag_category_id)
}

const findPlaybookForUser = (user, playbooks) => {
  const playbook = playbooks.find((pb) => pb.cid === user.config_cid)

  return playbook ? playbook.name : ''
}

const buildUncategorizedTagsForUser = (user, tagCategories) => {
  return user.tags.map((tag) => {
    if (!findTagCategoryForTag(tag, tagCategories)) {
      return {
        ...tag,
        userId: user.id,
        value: tag.name,
      }
    }

    return null
  })
}

const buildTagsForUser = (user, tagCategories) => {
  const userCopy = { ...user }
  userCopy.tags.forEach((tag) => {
    const foundTag = tagCategories.find((category) => category.id === tag.tag_category_id)
    if (foundTag) {
      userCopy[foundTag.name] = {
        ...tag,
        userId: user.id,
        value: tag.name,
      }
    }
  })

  return userCopy
}

export const buildUserTableColumns = ({
  playbooks,
  tags,
  tagCategories,
  flags,
  contentCustomizationEnabled,
  openRowModal,
}) => {
  const tagCategoryColumns = tagCategories.map((category) => {
    const categoryTags = tags.filter((tag) => tag.tag_category_id === category.id)

    return {
      accessor: category.name,
      label: category.name,
      isSearchable: true,
      searchSchema: {
        type: 'multiSelect',
        label: category.name,
        options: categoryTags.map((tag) => ({ label: tag.name, value: tag.name })),
        ...COLUMN_FILTER_PROPS,
      },
      format: (tag) => {
        if (tag) {
          return (
            <Pill small key={tag.userId}>
              {tag.name}
            </Pill>
          )
        }

        return null
      },
    }
  })

  let columns = [
    {
      accessor: 'full_name',
      label: 'Name',
      isSearchable: true,
      sticky: true,
      format: (fullName, row) => {
        return (
          <div className="flex-align-center small-gap">
            <span>{fullName}</span>
            <Button
              basic
              xs
              square
              tooltip="View Change History"
              aria-label={`View Change History for ${fullName}`}
              onClick={() => openRowModal('changeHistory', row)}
              data-testid={`view-change-history-${row.id}`}
            >
              <IconReplace />
            </Button>
          </div>
        )
      },
    },
    { accessor: 'username', isSearchable: true, label: 'Email' },
    {
      accessor: 'status',
      label: 'Status',
      isSearchable: true,
      searchSchema: {
        type: 'multiSelect',
        label: 'Status',
        options: [
          { label: 'Active', value: 'Active' },
          { label: 'Deactivated', value: 'Deactivated' },
        ],
        ...COLUMN_FILTER_PROPS,
      },
      format: (statusValue) => {
        if (statusValue === 'Active') {
          return (
            <Pill small outlined success>
              {statusValue}
            </Pill>
          )
        }

        return <Pill small>{statusValue}</Pill>
      },
    },
    {
      accessor: 'playbook',
      isSearchable: true,
      label: 'Playbook',
      searchSchema: {
        type: 'multiSelect',
        label: 'Playbook',
        options: playbooks.map((playbook) => ({ label: playbook.name, value: playbook.name })),
        ...COLUMN_FILTER_PROPS,
      },
      format: (playbookValue) => {
        const playbook = playbooks.find((p) => p.name === playbookValue || p.id === playbookValue)
        return (
          <div className="flex-align-center flex-space-between full-width">
            <span>{playbookValue}</span>
            {playbook && !playbook.config_agent_app_visibility && (
              <InteractiveTooltip
                content="This playbook is invisible to the agent. To change the visibility settings, please navigate to the Playbooks page"
                placement="top"
              >
                <span data-testid="playbook-hidden-icon">
                  <IconEyeExclamation className="icon-svg status-warning" stroke={2} />
                </span>
              </InteractiveTooltip>
            )}
          </div>
        )
      },
    },
    { accessor: 'code', isSearchable: true, label: 'VoIP User ID' },
    { accessor: 'external_system_id', isSearchable: true, label: 'External System ID' },
    {
      accessor: 'access',
      label: 'Access',
      isSearchable: true,
      searchSchema: {
        type: 'multiSelect',
        label: 'Access',
        options: cloudAccessSchema.options,
        ...COLUMN_FILTER_PROPS,
      },
      format: (accessList) => {
        if (accessList.length === 0) {
          return null
        }

        const visible = accessList.slice(0, MAX_VISIBLE_PERMISSIONS)
        const overflowCount = accessList.length - MAX_VISIBLE_PERMISSIONS

        return (
          <div className="access-pills-wrap">
            {visible.map((permission) => (
              <Pill small key={permission.value}>
                {ACCESS_SHORT_LABELS[permission.label] || permission.label}
              </Pill>
            ))}
            {overflowCount > 0 && (
              <InteractiveTooltip
                placement="top-start"
                content={accessList.map((permission) => permission.label).join(', ')}
              >
                <span>
                  <Pill small>+{overflowCount} more</Pill>
                </span>
              </InteractiveTooltip>
            )}
          </div>
        )
      },
    },
  ]

  if (flags?.cloudTopAgentToggle2022) {
    columns.push({
      accessor: 'is_top_agent',
      label: 'Customization Enabled',
      format: (isTopAgent) => {
        return <div>{isTopAgent && contentCustomizationEnabled ? 'Yes' : 'No'}</div>
      },
    })
  }

  const uncategorizedTagOptions = tags
    .filter((tag) => !tagCategories.some((category) => category.id === tag.tag_category_id))
    .map((tag) => ({ label: tag.name, value: tag.name }))

  columns.push({
    accessor: 'uncategorized_tags',
    label: 'Uncategorized Tags',
    isSearchable: true,
    searchSchema: {
      type: 'multiSelect',
      label: 'Uncategorized Tags',
      options: uncategorizedTagOptions,
      ...COLUMN_FILTER_PROPS,
    },
    format: (userTags) => {
      const visibleTags = userTags.filter(
        (tag) => tag && !findTagCategoryForTag(tag, tagCategories)
      )

      if (visibleTags.length === 0) {
        return null
      }

      if (visibleTags.length === 1) {
        return <Pill small>{visibleTags[0].name}</Pill>
      }

      const remainingCount = visibleTags.length - 1

      return (
        <div className="flex-align-center small-gap">
          <Pill small>{visibleTags[0].name}</Pill>
          <InteractiveTooltip
            placement="top-start"
            content={visibleTags.map((tag) => tag.name).join(', ')}
          >
            <span>
              <Pill small>+{remainingCount} more</Pill>
            </span>
          </InteractiveTooltip>
        </div>
      )
    },
  })

  columns = columns.concat(tagCategoryColumns)

  return columns
}

export const buildRows = (users, { playbooks, tagCategories, agentGPTEnabled }) => {
  return users.map((user) => {
    const userWithTags = buildTagsForUser(user, tagCategories)

    const userAccess = cloudAccessSchema.options.filter((option) => {
      if (option.value === 'cloud_access') {
        return !!user.password
      }

      // The chip would otherwise show a grant the org cannot use.
      if (option.value === 'verified_answer_editor' && !agentGPTEnabled) {
        return false
      }

      return !!user[option.value]
    })

    return {
      ...userWithTags,
      full_name: `${user.first_name} ${user.last_name}`,
      status: user.deleted ? 'Deactivated' : 'Active',
      playbook: findPlaybookForUser(user, playbooks),
      uncategorized_tags: buildUncategorizedTagsForUser(user, tagCategories),
      access: userAccess,
    }
  })
}
